import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { ArticleListProps } from './contracts';
import ArticleListUI from './ArticleListUI';
import { useArticlesQuery, useInfiniteArticlesQuery } from '@data/articles/useArticlesQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader/SkeletonLoader';
import { Article, PaginatedResponse } from '@repo/types';
import { trackFilter } from '../../../utils/tracking';
import { joinTags, useFilterTracking, useUrlFilterValues } from '../../../utils/filterSelections';

const FILTER_DEBOUNCE_MS = 500;

type ArticleTagSources = {
  extraTags?: string;
  teamFromSlug?: boolean;
  teamSlug?: string;
  urlTags?: string;
  filterByTags?: boolean;
  rawUrlTags: string | null;
};

/**
 * Tags come from several places at once: the component's CMS config, the team
 * slug on team pages, and the FeaturedTags chips in the URL. Club and division
 * are deliberately absent — they go to the API as their own params.
 */
const buildArticleTags = ({
  extraTags,
  teamFromSlug,
  teamSlug,
  urlTags,
  filterByTags,
  rawUrlTags,
}: ArticleTagSources): string | undefined => {
  // Fallback to filterByTags behavior for backwards compatibility
  const chipTags = urlTags ?? (filterByTags ? rawUrlTags : undefined);

  return joinTags([extraTags, teamFromSlug ? teamSlug : undefined, chipTags]);
};

export function ArticleList({
  teamFromSlug,
  startDate,
  endDate,
  tags: extraTags,
  tagsMatchMode,
  excludeTags,
  excludeIds,
  filterByTags,
  readFiltersFromUrl,
  extraData,
  sort,
  order,
  limit = 1,
  lazyLoad = false,
  ...props
}: ArticleListProps) {
  const [searchParams] = useSearchParams();
  const offset =
    searchParams.get('offset') && typeof parseInt(searchParams.get('offset') as string) === 'number'
      ? parseInt(searchParams.get('offset') as string)
      : 1;

  // Club and division are sent to the API as their own params rather than
  // merged into tags here: the API resolves them (including the literal "all",
  // which merging turned into a search for a tag named "all").
  const { raw, tags: urlTags, club, division, search: query } = useUrlFilterValues(readFiltersFromUrl);

  const tags = buildArticleTags({
    extraTags,
    teamFromSlug,
    teamSlug: extraData?.team?.slug,
    urlTags,
    filterByTags,
    rawUrlTags: raw.tags,
  });

  const queryArgs = useMemo(
    () => ({
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      tags,
      tagsMatchMode: tagsMatchMode ?? undefined,
      limit: limit ?? undefined,
      sort: sort ?? undefined,
      order: order ?? undefined,
      excludeIds: excludeIds ?? undefined,
      excludeTags: excludeTags ?? undefined,
      query,
      club,
      division,
    }),
    [startDate, endDate, tags, tagsMatchMode, limit, sort, order, excludeIds, excludeTags, query, club, division]
  );

  // Debounce query args when readFiltersFromUrl is enabled to avoid firing queries on every filter change
  const [debouncedQueryArgs] = useDebounce(queryArgs, readFiltersFromUrl ? FILTER_DEBOUNCE_MS : 0, { leading: true });

  useFilterTracking(readFiltersFromUrl, raw, trackFilter.articleFilterComplete);

  const paginatedQueryArgs = useMemo(() => ({ ...debouncedQueryArgs, page: offset }), [debouncedQueryArgs, offset]);

  // Use regular query when lazyLoad is disabled
  const regularQuery = useArticlesQuery(paginatedQueryArgs, { enabled: !lazyLoad });

  // Use infinite query when lazyLoad is enabled
  const infiniteQuery = useInfiniteArticlesQuery(debouncedQueryArgs, { enabled: lazyLoad });

  const isLoading = lazyLoad ? infiniteQuery.isLoading : regularQuery.isLoading;
  const error = lazyLoad ? infiniteQuery.error : regularQuery.error;

  // Flatten all articles from infinite query pages
  const allArticles = useMemo(() => {
    if (!lazyLoad || !infiniteQuery.data) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.items);
  }, [lazyLoad, infiniteQuery.data]);

  // Create a synthetic PaginatedResponse for the UI component
  const paginatedArticles: PaginatedResponse<Article> | undefined = useMemo(() => {
    if (lazyLoad && infiniteQuery.data?.pages && infiniteQuery.data.pages.length > 0) {
      const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
      if (!lastPage) return regularQuery.data;
      return {
        items: allArticles,
        pagination: lastPage.pagination,
      };
    }
    return regularQuery.data;
  }, [lazyLoad, infiniteQuery.data, allArticles, regularQuery.data]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={limit * 5 - 1} />
      </div>
    );
  }

  if (!paginatedArticles || error) {
    return null;
  }

  return (
    <ArticleListUI
      paginatedArticles={paginatedArticles}
      allArticles={allArticles}
      lazyLoad={lazyLoad}
      hasNextPage={infiniteQuery.hasNextPage}
      isFetchingNextPage={infiniteQuery.isFetchingNextPage}
      fetchNextPage={infiniteQuery.fetchNextPage}
      {...props}
    />
  );
}
